/**
 * Document Center Component
 * 
 * Dashboard tab for managing user documents with auto-fill.
 * Allows experts to select templates, auto-fill with case data, edit fields, and download PDFs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #0a0d14;
  color: #e8eaed;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px 20px;
  background: rgba(17, 24, 39, 0.98);
  border-bottom: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
    
    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
  ` : css`
    background: rgba(55, 65, 81, 0.5);
    border: 1px solid rgba(75, 85, 99, 0.5);
    color: #d1d5db;
    
    &:hover:not(:disabled) {
      background: rgba(75, 85, 99, 0.5);
      border-color: rgba(99, 102, 241, 0.5);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  border-right: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid rgba(75, 85, 99, 0.2);
  background: rgba(17, 24, 39, 0.5);
`;

const SidebarTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #a5b4fc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DocumentList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const DocumentCard = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 6px;
  overflow: hidden;
  
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.2)' : 'rgba(31, 41, 55, 0.4)'};
  border: 1px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.5)' : 'transparent'};
  
  &:hover {
    background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.25)' : 'rgba(45, 55, 72, 0.5)'};
  }
`;

const DocumentName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #e8eaed;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  flex-wrap: wrap;
  
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }
`;

const StatusBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.$status === 'complete' && css`
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
  `}
  
  ${props => props.$status === 'draft' && css`
    background: rgba(245, 158, 11, 0.2);
    color: #fbbf24;
  `}
  
  ${props => props.$status === 'verified' && css`
    background: rgba(99, 102, 241, 0.2);
    color: #a5b4fc;
  `}
`;

const ProgressBar = styled.div`
  height: 4px;
  background: rgba(75, 85, 99, 0.3);
  border-radius: 2px;
  margin-top: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => {
    if (props.$percent >= 100) return '#10b981';
    if (props.$percent >= 50) return '#fbbf24';
    return '#ef4444';
  }};
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.5);
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.4;
`;

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
`;

const EmptyText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  max-width: 360px;
`;

const FieldsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
`;

const FieldStatus = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  
  ${props => props.$status === 'auto_filled' && css`
    background: rgba(236, 72, 153, 0.2);
    color: #f472b6;
  `}
  
  ${props => props.$status === 'manual' && css`
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
  `}
  
  ${props => props.$status === 'verified' && css`
    background: rgba(99, 102, 241, 0.2);
    color: #a5b4fc;
  `}
  
  ${props => props.$status === 'missing' && css`
    background: rgba(239, 68, 68, 0.2);
    color: #f87171;
  `}
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.$missing ? 'rgba(239, 68, 68, 0.5)' : 'rgba(75, 85, 99, 0.5)'};
  background: rgba(17, 24, 39, 0.8);
  color: #e8eaed;
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const FieldsHeader = styled.div`
  padding: 16px 20px;
  background: rgba(17, 24, 39, 0.5);
  border-bottom: 1px solid rgba(75, 85, 99, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FieldsTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const FieldsStats = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const Stat = styled.span`
  color: ${props => props.$color || 'inherit'};
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 40px auto;
`;

const ActionBar = styled.div`
  padding: 12px 20px;
  background: rgba(17, 24, 39, 0.98);
  border-top: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

// ============================================
// TEMPLATE SELECTOR MODAL
// ============================================

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #1a1f2e;
  border: 1px solid rgba(75, 85, 99, 0.4);
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 70vh;
  overflow-y: auto;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 20px 0;
`;

const TemplateOption = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.2)' : 'rgba(31, 41, 55, 0.5)'};
  border: 1px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(75, 85, 99, 0.3)'};
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const TemplateName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #e8eaed;
`;

const TemplateDetails = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
  display: flex;
  gap: 12px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

// ============================================
// COMPONENT
// ============================================

const DocumentCenter = ({ selectedUser, threadId }) => {
  const axios = useAuthenticatedAxios();
  const { success, error: showError } = useToast();
  const { confirm } = useConfirm();
  
  // State
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  
  // Modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Load documents for this thread
  const loadDocuments = useCallback(async () => {
    if (!threadId) return;
    
    try {
      const response = await axios.get(`/user-documents/thread/${threadId}`);
      if (response.data.success) {
        setDocuments(response.data.documents);
        // Select first document if none selected
        if (response.data.documents.length > 0 && !selectedDoc) {
          setSelectedDoc(response.data.documents[0]);
        }
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }, [axios, threadId, selectedDoc]);
  
  // Load available templates
  const loadTemplates = useCallback(async () => {
    try {
      const response = await axios.get('/user-documents/templates');
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  }, [axios]);
  
  useEffect(() => {
    if (threadId) {
      setLoading(true);
      setSelectedDoc(null);
      setDocuments([]);
      loadDocuments();
      loadTemplates();
    }
  }, [threadId]);
  
  // Create new document with auto-fill
  const handleCreateDocument = async () => {
    if (!selectedTemplate || !threadId) return;
    
    setCreating(true);
    
    try {
      const response = await axios.post('/user-documents', {
        threadId,
        templateId: selectedTemplate._id,
        userName: selectedUser?.name || selectedUser?.userName
      });
      
      if (response.data.success) {
        setDocuments(prev => [response.data.document, ...prev]);
        setSelectedDoc(response.data.document);
        setShowTemplateModal(false);
        setSelectedTemplate(null);
      }
    } catch (error) {
      console.error('Error creating document:', error);
      showError('Failed to create document');
    } finally {
      setCreating(false);
    }
  };
  
  // Handle field value change
  const handleFieldChange = (fieldName, value) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
  
  // Save field changes
  const handleSave = async () => {
    if (!selectedDoc || Object.keys(editedFields).length === 0) return;
    
    setSaving(true);
    
    try {
      const fields = Object.entries(editedFields).map(([fieldName, value]) => ({
        fieldName,
        value
      }));
      
      const response = await axios.put(`/user-documents/${selectedDoc._id}`, { fields });
      
      if (response.data.success) {
        setSelectedDoc(response.data.document);
        // Update in list
        setDocuments(prev => prev.map(d => 
          d._id === response.data.document._id ? response.data.document : d
        ));
        setEditedFields({});
      }
    } catch (error) {
      console.error('Error saving document:', error);
      showError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };
  
  // Refresh auto-fill
  const [refreshing, setRefreshing] = useState(false);
  
  const handleRefreshAutoFill = async () => {
    if (!selectedDoc) return;
    
    setRefreshing(true);
    
    try {
      const response = await axios.post(`/user-documents/${selectedDoc._id}/refresh`);
      
      if (response.data.success) {
        setSelectedDoc(response.data.document);
        // Update in list
        setDocuments(prev => prev.map(d => 
          d._id === response.data.document._id ? response.data.document : d
        ));
        setEditedFields({});
        success(response.data.message || 'Auto-fill refreshed successfully');
      }
    } catch (error) {
      console.error('Error refreshing auto-fill:', error);
      showError('Failed to refresh auto-fill');
    } finally {
      setRefreshing(false);
    }
  };
  
  // Download PDF
  const handleDownload = async () => {
    if (!selectedDoc) return;
    
    try {
      const response = await axios.get(`/user-documents/${selectedDoc._id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedDoc.formCode}_${selectedDoc.userName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Failed to download PDF');
    }
  };
  
  // Delete document
  const handleDelete = async (docId) => {
    const confirmed = await confirm({
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await axios.delete(`/user-documents/${docId}`);
      setDocuments(prev => prev.filter(d => d._id !== docId));
      if (selectedDoc?._id === docId) {
        setSelectedDoc(null);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showError('Failed to delete document');
    }
  };
  
  // Get current field value (edited or original)
  const getFieldValue = (field) => {
    if (editedFields.hasOwnProperty(field.fieldName)) {
      return editedFields[field.fieldName];
    }
    return field.value || '';
  };
  
  if (!threadId) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>📋</EmptyIcon>
          <EmptyTitle>No User Selected</EmptyTitle>
          <EmptyText>
            Select a user from the User List to view and manage their documents.
          </EmptyText>
        </EmptyState>
      </Container>
    );
  }
  
  return (
    <Container>
      <Header>
        <HeaderTitle>
          <span>📋</span>
          Form Center
          {selectedUser && (
            <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.6)' }}>
              — {selectedUser.name || selectedUser.userName}
            </span>
          )}
        </HeaderTitle>
        <HeaderActions>
          {templates.length > 0 && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginRight: 8 }}>
              {templates.length} templates available
            </span>
          )}
          <Button $primary onClick={() => setShowTemplateModal(true)}>
            + New Form
          </Button>
        </HeaderActions>
      </Header>
      
      <Content>
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Forms ({documents.length})</SidebarTitle>
          </SidebarHeader>
          
          <DocumentList>
            {loading ? (
              <LoadingSpinner />
            ) : documents.length === 0 ? (
              <EmptyState style={{ padding: '40px 20px' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📄</div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>No documents yet</div>
                <Button 
                  $primary 
                  onClick={() => setShowTemplateModal(true)}
                  style={{ fontSize: 11, padding: '6px 12px' }}
                >
                  + Create Document
                </Button>
                {templates.length > 0 && (
                  <div style={{ fontSize: 10, marginTop: 8, color: 'rgba(255,255,255,0.4)' }}>
                    {templates.length} templates available
                  </div>
                )}
              </EmptyState>
            ) : (
              documents.map(doc => (
                <DocumentCard
                  key={doc._id}
                  $selected={selectedDoc?._id === doc._id}
                  onClick={() => {
                    setSelectedDoc(doc);
                    setEditedFields({});
                  }}
                >
                  <DocumentName>{doc.templateName}</DocumentName>
                  <DocumentMeta>
                    <span>{doc.formCode}</span>
                    <StatusBadge $status={doc.status}>{doc.status}</StatusBadge>
                  </DocumentMeta>
                  <ProgressBar>
                    <ProgressFill $percent={doc.completionPercent} />
                  </ProgressBar>
                </DocumentCard>
              ))
            )}
          </DocumentList>
        </Sidebar>
        
        <MainArea>
          {!selectedDoc ? (
            <EmptyState>
              <EmptyIcon>📝</EmptyIcon>
              <EmptyTitle>Select a Document</EmptyTitle>
              <EmptyText>
                Choose a document from the list to view and edit its fields, or create a new one.
              </EmptyText>
            </EmptyState>
          ) : (
            <>
              <FieldsHeader>
                <FieldsTitle>
                  {selectedDoc.templateName}
                  <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.5)', marginLeft: 8 }}>
                    ({selectedDoc.formCode})
                  </span>
                </FieldsTitle>
                <FieldsStats>
                  <Stat $color="#34d399">{selectedDoc.filledCount} filled</Stat>
                  <Stat $color="#f87171">{selectedDoc.missingCount} missing</Stat>
                  <Stat>{selectedDoc.completionPercent}% complete</Stat>
                </FieldsStats>
              </FieldsHeader>
              
              <FieldsContainer>
                {selectedDoc.filledFields.map(field => (
                  <FieldGroup key={field.fieldName}>
                    <FieldLabel>
                      {field.displayName}
                      <FieldStatus $status={field.status}>
                        {field.status === 'auto_filled' && 'Auto-filled'}
                        {field.status === 'manual' && 'Manual'}
                        {field.status === 'verified' && 'Verified'}
                        {field.status === 'missing' && 'Missing'}
                      </FieldStatus>
                    </FieldLabel>
                    <FieldInput
                      value={getFieldValue(field)}
                      onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                      placeholder={`Enter ${field.displayName.toLowerCase()}...`}
                      $missing={field.status === 'missing' && !editedFields[field.fieldName]}
                    />
                  </FieldGroup>
                ))}
              </FieldsContainer>
              
              <ActionBar>
                <Button onClick={() => handleDelete(selectedDoc._id)}>
                  Delete
                </Button>
                <Button 
                  onClick={handleRefreshAutoFill}
                  disabled={refreshing}
                  title="Re-run auto-fill with latest case data"
                >
                  {refreshing ? 'Refreshing...' : '🔄 Refresh Auto-Fill'}
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={Object.keys(editedFields).length === 0 || saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button $primary onClick={handleDownload}>
                  Download PDF
                </Button>
              </ActionBar>
            </>
          )}
        </MainArea>
      </Content>
      
      {/* Template Selection Modal */}
      {showTemplateModal && (
        <ModalOverlay onClick={() => !creating && setShowTemplateModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>Select Document Template</ModalTitle>
            
            {templates.length === 0 ? (
              <EmptyState style={{ padding: '30px 0' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📄</div>
                <div>No templates available</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Upload templates in Profile → Forms Templates
                </div>
              </EmptyState>
            ) : (
              templates.map(template => (
                <TemplateOption
                  key={template._id}
                  $selected={selectedTemplate?._id === template._id}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <TemplateName>{template.name}</TemplateName>
                  <TemplateDetails>
                    <span>{template.formCode}</span>
                    <span>{template.category}</span>
                    <span>{template.totalFields} fields</span>
                  </TemplateDetails>
                </TemplateOption>
              ))
            )}
            
            <ModalActions>
              <Button onClick={() => setShowTemplateModal(false)} disabled={creating}>
                Cancel
              </Button>
              <Button 
                $primary 
                onClick={handleCreateDocument}
                disabled={!selectedTemplate || creating}
              >
                {creating ? 'Creating...' : 'Create & Auto-Fill'}
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default DocumentCenter;

